import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useConfirm } from '../components/ConfirmDialog';
import { askToNotify, notifyState } from '../lib/osNotify';
import { ping, unlock } from '../lib/chime';
import {
  useNotifyPrefs, saveNotifyPrefs, resetNotifyPrefs, localSound, setMasterSound
} from '../lib/notifyPrefs';
import NotificationPush from './NotificationPush';
import './NotificationSettings.css';

/**
 * Notifications (migration 117): what Jarvis tells you about, and how.
 *
 * Every kind of notice Jarvis gives is a row, grouped the way the server's
 * catalogue groups them (backend lib/notify-prefs.js - the page draws
 * whatever it is sent, so a new kind needs no change here), with up to four
 * switches:
 *
 *   Bell             the line in the bell, and so the number on the icon
 *   Email            an email about it; "Always" where it cannot be turned
 *                    off (a sign-in code, a calendar invitation)
 *   Sound & pop-up   the chime and the operating system's pop-up while
 *                    Jarvis is open
 *   Push             pushed to the person's devices, Jarvis open or not
 *                    (129; the column is there once the migration has run)
 *
 * A dash is a channel that kind does not have. Each switch saves the moment
 * it is pressed and says so; a save that fails puts the switch back and says
 * why. Above them, the master sound switch (the one the call screen's
 * speaker button has always been - now kept with the person, not the
 * browser) and whether this browser may show pop-ups at all; and "Push to
 * your devices" (NotificationPush.js): turn on this device, the devices
 * already on, and quiet hours.
 *
 * Staff, administrators and contractors all have it, under their name in the
 * account menu; the footer of every Jarvis email links here.
 */

const CHANNELS = [
  { key: 'bell', label: 'Bell', short: 'Bell', long: 'In the bell' },
  { key: 'email', label: 'Email', short: 'Email', long: 'By email' },
  { key: 'sound', label: 'Sound & pop-up', short: 'Sound', long: 'Sound and pop-up' },
  { key: 'push', label: 'Push', short: 'Push', long: 'Pushed to your devices' }
];

const REQUESTER = ['ticket_created', 'ticket_reply', 'ticket_resolved'];

const Icon = {
  bell: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4.5 12.6V8.4a4.5 4.5 0 0 1 9 0v4.2l1.3 1.6H3.2Z" /><path d="M7.4 15.6a1.7 1.7 0 0 0 3.2 0" />
    </svg>
  ),
  email: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.2" y="3.8" width="13.6" height="10.4" rx="1.8" /><path d="m2.8 5 6.2 4.6L15.2 5" />
    </svg>
  ),
  sound: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7h2.6L9 4.2v9.6L5.6 11H3Z" /><path d="M11.8 6.6a3.3 3.3 0 0 1 0 4.8M13.8 4.8a6 6 0 0 1 0 8.4" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.2" y="7.2" width="9.6" height="6.6" rx="1.4" /><path d="M5.4 7.2V5.2a2.6 2.6 0 0 1 5.2 0v2" />
    </svg>
  ),
  push: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="1.8" width="8" height="14.4" rx="1.8" /><path d="M8 13.6h2" />
    </svg>
  ),
  popup: (
    <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.2" y="3" width="13.6" height="9.6" rx="1.6" /><path d="M6.4 15.4h5.2M9 12.6v2.8" /><rect x="9.4" y="5" width="4.6" height="2.8" rx="0.8" />
    </svg>
  )
};

/** One switch: a real button with role="switch", labelled for a screen reader. */
function Switch({ on, onChange, label, disabled, busy, testid }) {
  return (
    <button type="button" role="switch" aria-checked={Boolean(on)} aria-label={label}
            className={`ns-switch${on ? ' is-on' : ''}${busy ? ' is-busy' : ''}`}
            disabled={disabled} onClick={onChange} data-testid={testid}>
      <span className="ns-knob" />
    </button>
  );
}

/** One kind's cell for one channel. */
function Cell({ kind, channel, state, onToggle, disabled, busy }) {
  const c = kind[channel.key] || { available: false };
  let body;
  if (!c.available) {
    body = <span className="ns-na" aria-label={`No ${channel.long.toLowerCase()} for this`}>—</span>;
  } else if (c.locked) {
    body = (
      <span className="ns-always" title="Always sent" data-testid={`ns-${kind.key}-${channel.key}-locked`}>
        {Icon.lock}<span>Always</span>
      </span>
    );
  } else {
    body = (
      <Switch on={state} disabled={disabled} busy={busy}
              label={`${kind.label}: ${channel.long.toLowerCase()}`}
              onChange={() => onToggle(kind, channel.key, !state)}
              testid={`ns-${kind.key}-${channel.key}`} />
    );
  }
  return (
    <div className={`ns-cell${c.available ? '' : ' is-na'}`} data-channel={channel.key}>
      <span className="ns-cell-label" aria-hidden="true">{Icon[channel.key]}<span>{channel.short}</span></span>
      {body}
    </div>
  );
}

function popupWords(state) {
  switch (state) {
    case 'granted': return { tone: 'success', text: 'Allowed in this browser.' };
    case 'denied': return { tone: 'warning', text: 'Blocked in this browser. Allow notifications for this site in the browser’s settings to see them.' };
    case 'unsupported': return { tone: 'info', text: 'This browser cannot show them.' };
    default: return { tone: 'info', text: 'This browser has not been asked yet.' };
  }
}

export default function NotificationSettings() {
  const prefs = useNotifyPrefs();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  const [popup, setPopup] = useState(notifyState);
  const [sound, setSound] = useState(localSound);
  const [confirm, confirmDialog] = useConfirm();
  const saidTimer = useRef(null);

  useEffect(() => {
    if (prefs && prefs.data) {
      setData(prefs.data);
      if (prefs.data.ready && typeof prefs.data.sound === 'boolean') setSound(prefs.data.sound);
    }
  }, [prefs]);
  useEffect(() => () => clearTimeout(saidTimer.current), []);

  const loadFailed = Boolean(prefs && !prefs.data && prefs.error);
  const readOnly = Boolean(data && (data.on_behalf || !data.ready));

  const say = (text) => {
    setSaid(text);
    clearTimeout(saidTimer.current);
    saidTimer.current = setTimeout(() => setSaid(''), 2600);
  };
  const failed = (e, fallback) => {
    setError(e?.response?.data?.error || fallback);
    setSaid('');
  };

  /** One channel of one kind: shown at once, saved, put back if refused. */
  const toggle = async (kind, channel, next) => {
    const id = `${kind.key}.${channel}`;
    const before = data;
    setError('');
    setBusy((b) => ({ ...b, [id]: true }));
    setData((d) => ({
      ...d,
      kinds: d.kinds.map((k) => (k.key === kind.key ? { ...k, [channel]: { ...k[channel], on: next } } : k))
    }));
    try {
      const fresh = await saveNotifyPrefs({ kinds: { [kind.key]: { [channel]: next } } });
      if (fresh) setData(fresh);
      say('Saved');
    } catch (e) {
      setData(before);
      failed(e, 'That could not be saved. Try again in a moment.');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const setAllRequester = async (on) => {
    setError('');
    setBusy((b) => ({ ...b, requester: true }));
    try {
      const fresh = await saveNotifyPrefs({ kinds: Object.fromEntries(REQUESTER.map((k) => [k, { email: on }])) });
      if (fresh) setData(fresh);
      say(on ? 'Saved — you will get emails about the tickets you raise' : 'Saved');
    } catch (e) {
      failed(e, 'That could not be saved. Try again in a moment.');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n.requester; return n; });
    }
  };

  const toggleSound = async () => {
    const next = !sound;
    setSound(next);
    setError('');
    if (!data || !data.ready || data.on_behalf) {
      // Before 117, or nobody to tell: this browser remembers it, as it always did.
      setMasterSound(next);
      say('Saved in this browser');
      return;
    }
    setBusy((b) => ({ ...b, sound: true }));
    try {
      const fresh = await saveNotifyPrefs({ sound: next });
      if (fresh) setData(fresh);
      say('Saved');
      if (next) { unlock(); }
    } catch (e) {
      setSound(!next);
      failed(e, 'That could not be saved. Try again in a moment.');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n.sound; return n; });
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: 'Put every notification back to how Jarvis sets it?',
      message: 'The bell, sounds, pushes and the usual emails come back as Jarvis sets them, and the emails about tickets you raise go back off. Your devices and quiet hours stay as they are.',
      confirmLabel: 'Reset'
    });
    if (!ok) return;
    setError('');
    try {
      const fresh = await resetNotifyPrefs();
      if (fresh) { setData(fresh); if (typeof fresh.sound === 'boolean') setSound(fresh.sound); }
      say('Back to the defaults');
    } catch (e) {
      failed(e, 'That could not be reset. Try again in a moment.');
    }
  };

  const allowPopups = () => { askToNotify().finally(() => setPopup(notifyState())); };
  const testSound = () => { unlock(); setTimeout(() => ping(null), 60); };

  const groups = useMemo(() => {
    if (!data) return [];
    return (data.groups || []).map((g) => ({ ...g, kinds: (data.kinds || []).filter((k) => k.group === g.key) }))
      .filter((g) => g.kinds.length);
  }, [data]);

  // The Push column once 129 has run; before it, three columns as before.
  const channels = CHANNELS.filter((c) => c.key !== 'push' || Boolean(data?.push?.ready));
  const requesterKinds = (data?.kinds || []).filter((k) => REQUESTER.includes(k.key) && k.email?.available);
  const requesterOn = requesterKinds.filter((k) => k.email.on).length;
  const pw = popupWords(popup);

  return (
    <div className={`container ns-page${channels.length === 4 ? ' has-push' : ''}`} data-testid="notification-settings">
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-description">
            Choose what Jarvis tells you about, and how: in the bell, by email, or with a sound and a pop-up
            while Jarvis is open. Your choices follow you to every browser and device you sign in on.
          </p>
        </div>
      </div>

      {data?.on_behalf && (
        <p className="as-note" data-tone="info" data-testid="ns-on-behalf">
          You are viewing somebody else’s notifications. Only they can change them.
        </p>
      )}
      {data && !data.ready && (
        <p className="as-note" data-tone="warning" data-testid="ns-not-ready">
          Notification choices are not available on this server yet (migration 117 has not run).
          Jarvis notifies you as it always has; the sound switch below is kept in this browser.
        </p>
      )}
      {data && data.mail && data.mail.configured === false && (
        <p className="as-note" data-tone="warning" data-testid="ns-no-mail">
          Jarvis is not sending emails from this server at the moment, so the email choices below will not
          send anything until it is.{data.mail.reason ? ` (${data.mail.reason}.)` : ''}
        </p>
      )}
      {loadFailed && (
        <p className="as-note" data-tone="warning" role="alert" data-testid="ns-load-failed">
          Your notification settings could not be loaded. The sound switch below still works in this browser.
        </p>
      )}

      <section className="ns-card ns-device" aria-labelledby="ns-device-h">
        <h2 id="ns-device-h" className="ns-h2">Sound and pop-ups</h2>
        <div className="ns-device-row">
          <span className="ns-device-ic">{Icon.sound}</span>
          <div className="ns-device-text">
            <strong>Sounds in Jarvis</strong>
            <span>The ping for a message, the ring for a call, the chime when a reminder goes off. Off silences all of them.</span>
            {sound && (
              <button type="button" className="ns-test" onClick={testSound} data-testid="ns-test-sound">
                Play a test sound
              </button>
            )}
          </div>
          <div className="ns-device-act">
            <Switch on={sound} onChange={toggleSound} label="Sounds in Jarvis" busy={busy.sound}
                    disabled={Boolean(data?.on_behalf)} testid="ns-master-sound" />
          </div>
        </div>
        <div className="ns-device-row">
          <span className="ns-device-ic">{Icon.popup}</span>
          <div className="ns-device-text">
            <strong>Pop-ups from this browser</strong>
            <span data-tone={pw.tone} data-testid="ns-popup-state">{pw.text}</span>
          </div>
          <div className="ns-device-act">
            {popup === 'default' && (
              <button type="button" className="btn btn-outline btn-small" onClick={allowPopups} data-testid="ns-allow-popups">
                Allow pop-ups
              </button>
            )}
          </div>
        </div>
      </section>

      {data && data.push && (
        <NotificationPush push={data.push} readOnly={Boolean(data.on_behalf)} onData={setData}
                          say={say} failed={failed} Switch={Switch} />
      )}

      {requesterKinds.length > 0 && requesterOn === 0 && !readOnly && (
        <section className="ns-callout" data-testid="ns-requester-callout">
          <span className="ns-callout-ic">{Icon.email}</span>
          <div>
            <strong>Emails about the tickets you raise</strong>
            <p>
              A confirmation with the reference, an email when somebody replies, and one when it is resolved.
              Off until you turn them on.
            </p>
          </div>
          <button type="button" className="btn btn-primary btn-small" disabled={Boolean(busy.requester)}
                  onClick={() => setAllRequester(true)} data-testid="ns-requester-on">
            Email me about my tickets
          </button>
        </section>
      )}

      {!data && !loadFailed && <p className="as-quiet ns-loading">Loading…</p>}

      {groups.map((g) => (
        <section key={g.key} className="ns-card ns-group" aria-labelledby={`ns-g-${g.key}`} data-group={g.key}>
          <div className="ns-group-head">
            <h2 id={`ns-g-${g.key}`} className="ns-h2">{g.label}</h2>
            {g.key !== 'always' && (
              <div className="ns-cols" aria-hidden="true">
                {channels.map((c) => <span key={c.key}>{c.label}</span>)}
              </div>
            )}
          </div>
          {g.key === 'always' && (
            <p className="ns-group-note">These come by email whatever you choose, because they are how something reaches you at all.</p>
          )}
          <ul className="ns-rows">
            {g.kinds.map((k) => (
              <li key={k.key} className={`ns-row${g.key === 'always' ? ' is-always' : ''}`} data-kind={k.key}>
                <div className="ns-what">
                  <strong>{k.label}</strong>
                  <span>{k.hint}</span>
                </div>
                <div className="ns-chans">
                  {channels.filter((c) => g.key !== 'always' || c.key === 'email').map((c) => (
                    <Cell key={c.key} kind={k} channel={c} state={Boolean(k[c.key]?.on)}
                          onToggle={toggle} disabled={readOnly} busy={Boolean(busy[`${k.key}.${c.key}`])} />
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {data && (
        <div className="ns-foot">
          <p className="ns-foot-note">
            The number on the Jarvis icon is set in the menu under your name.
            {data.audience !== 'contractor' && ' Instructions from IT can also come to your chat or to-dos: choose where on any instruction you are sent.'}
          </p>
          {!readOnly && (
            <button type="button" className="btn btn-plain btn-small" onClick={reset} data-testid="ns-reset">
              Reset to defaults
            </button>
          )}
        </div>
      )}

      <div className={`ns-toast${said || error ? ' is-shown' : ''}${error ? ' is-error' : ''}`}
           role={error ? 'alert' : 'status'} aria-live="polite" data-testid="ns-status">
        {error || said}
      </div>
      {confirmDialog}
    </div>
  );
}
